import { useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  InfoBar,
  Link,
  Modal,
  type ModalProps,
  NumberInput,
  ServiceError,
  Spinner,
} from "@shapr3d/frontend-lib";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import { teamsArticleURL } from "../../../config";
import {
  TeamSubscriptionsEstimateQueryKey,
  useTeamsSubscriptionsEstimate,
  useUpdateTeamSubscription,
} from "../../../hooks/useTeams";
import type {
  SeatBasedTeamSubscription,
  TeamsSubscriptionsEstimateResponseData,
} from "../../../hooks/useTeamsTypes";
import { ServiceErrorCode, SubscriptionTier } from "../../../types";
import { AddSeatsEstimateSummary } from "../../EstimateSummaries/AddSeatsEstimateSummary/AddSeatsEstimateSummary";
import { OverlayProvider } from "../../InfoTooltip/InfoTooltip";
import PaymentUpdateModal from "../../PaymentUpdateModal/PaymentUpdateModal";

import style from "./SubscriptionUpgradeModal.module.css";

export type SubscriptionUpgradeModalCopy = {
  title: string;
  submitButton: string;
  toastSuccess: string | ((seats: number | null) => string);
  errorTitle: string;
  errorText: string;
  paymentErrorTitle: string;
  paymentErrorText: string;
  paymentCTAText: string;
  tertiaryText: (args: {
    currencySymbol: string;
    proratedTotalAmount: string;
    renewalCurrencyCode: string;
    proratedCurrencyCode: string;
  }) => string;
  learnMore: string;
};

export const SubscriptionUpgradeModal: React.FC<
  Pick<ModalProps, "open" | "onClose"> & {
    subscription: SeatBasedTeamSubscription;
    teamID: string | undefined;
    tier?: SubscriptionTier;
    copy: SubscriptionUpgradeModalCopy;
    defaultAdditionalSeats?: number;
    seatCountLabel?: string;
    toApiSeatCount?: (value: number) => number | null;
    hideSeatSelector?: boolean;
    onSeatCountChange?: (count: number) => void;
    onLearnMoreClick?: () => void;
    onSubmitClick?: (seats: number | null) => void;
    onEstimateData?: (
      data: TeamsSubscriptionsEstimateResponseData | undefined,
      open: boolean
    ) => void;
  }
> = ({
  subscription,
  teamID,
  tier,
  copy,
  onClose,
  open,
  defaultAdditionalSeats = 1,
  seatCountLabel,
  toApiSeatCount,
  hideSeatSelector = false,
  onSeatCountChange,
  onLearnMoreClick,
  onSubmitClick,
  onEstimateData,
}) => {
  const [isPaymentUpdateModalOpen, setIsPaymentUpdateModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { watch, control, handleSubmit, reset } = useForm({
    defaultValues: { additionalEditorSeatCount: defaultAdditionalSeats },
  });

  const additionalEditorSeatCount = watch("additionalEditorSeatCount");
  const apiSeatCount = toApiSeatCount
    ? toApiSeatCount(additionalEditorSeatCount ?? 1)
    : additionalEditorSeatCount;

  const {
    data: estimateData,
    isLoading: isEstimateLoading,
    isFetching: isEstimateFetching,
    isPaused: isEstimatePaused,
    isPlaceholderData: isEstimatePreviousData,
    isError: isEstimateError,
  } = useTeamsSubscriptionsEstimate({
    subscription,
    additionalEditorSeatCount: apiSeatCount,
    tier,
  });

  const onEstimateDataRef = useRef(onEstimateData);
  useEffect(() => {
    onEstimateDataRef.current = onEstimateData;
  }, [onEstimateData]);

  useEffect(() => {
    onEstimateDataRef.current?.(estimateData, open);
  }, [estimateData, open]);

  const {
    mutateAsync: mutateUpdateTeamSubscription,
    isPending: isUpdateTeamSubscriptionPending,
    isError: isUpdateTeamSubscriptionError,
    reset: resetUpdateTeamSubscription,
  } = useUpdateTeamSubscription({ teamID, subscriptionID: subscription.id });

  const resetState = useCallback(() => {
    reset();
    setIsPaymentUpdateModalOpen(false);
    resetUpdateTeamSubscription();
    queryClient.invalidateQueries({ queryKey: TeamSubscriptionsEstimateQueryKey });
    onClose?.();
  }, [reset, setIsPaymentUpdateModalOpen, onClose, resetUpdateTeamSubscription, queryClient]);

  const submit = useCallback(async () => {
    try {
      await mutateUpdateTeamSubscription({ additionalEditorSeatCount: apiSeatCount, tier });
      resetState();
      toast.success(
        typeof copy.toastSuccess === "function"
          ? copy.toastSuccess(additionalEditorSeatCount)
          : copy.toastSuccess
      );
    } catch (error) {
      if (
        error instanceof ServiceError &&
        error.hasCode(ServiceErrorCode.PaymentProcessingFailed)
      ) {
        setIsPaymentUpdateModalOpen(true);
        return;
      }
    }
  }, [
    mutateUpdateTeamSubscription,
    resetState,
    apiSeatCount,
    tier,
    copy,
    additionalEditorSeatCount,
  ]);

  return (
    <>
      <Modal
        className={style.modal}
        open={open && !isPaymentUpdateModalOpen}
        onClose={() => {
          if (isPaymentUpdateModalOpen) {
            return;
          }
          resetState();
        }}
        title={copy.title}
      >
        <OverlayProvider>
          <form className={style.wrapper} onSubmit={handleSubmit(submit)}>
            {/* TODO: seat selector is hidden for studio upgrade — multi-seat upgrade not yet supported */}
            {!hideSeatSelector && (
              <Controller
                name="additionalEditorSeatCount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className={style.seatSelector}>
                    {seatCountLabel && <span>{seatCountLabel}</span>}
                    <NumberInput
                      min={1}
                      max={50}
                      onChange={(_, value) => {
                        onSeatCountChange?.(value ?? 1);
                        onChange(value);
                      }}
                      value={value}
                    />
                  </div>
                )}
              />
            )}

            {estimateData && (
              <AddSeatsEstimateSummary
                isLoading={isEstimateFetching}
                currencySymbol={estimateData.currencySymbol}
                proratedEstimate={estimateData?.prorated}
                renewalEstimate={estimateData?.renewal}
                renewalDate={estimateData?.renewalDate}
                additionalEditorSeatCount={apiSeatCount ?? 0}
                subscription={subscription}
                paymentSource={estimateData?.primaryPaymentSource}
                submitButtonText={copy.submitButton}
              />
            )}
            {isEstimateLoading && <Spinner size={40} />}
            {(isEstimateError || isUpdateTeamSubscriptionError) && (
              <InfoBar variant="warning" title={copy.errorTitle} text={copy.errorText} />
            )}

            <div className={style.ctaRow}>
              <Link
                href={teamsArticleURL}
                target="_blank"
                referrerPolicy="no-referrer"
                onClick={onLearnMoreClick}
              >
                {copy.learnMore}
              </Link>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  isEstimateFetching ||
                  isEstimatePaused ||
                  isEstimatePreviousData ||
                  isUpdateTeamSubscriptionPending ||
                  isEstimateError
                }
                onClick={() => onSubmitClick?.(additionalEditorSeatCount)}
              >
                {copy.submitButton}
              </Button>
            </div>
          </form>
        </OverlayProvider>
      </Modal>
      <PaymentUpdateModal
        teamID={teamID}
        open={isPaymentUpdateModalOpen}
        onClose={() => {
          setIsPaymentUpdateModalOpen(false);
        }}
        initialError={
          <InfoBar variant="critical" title={copy.paymentErrorTitle} text={copy.paymentErrorText} />
        }
        tertiaryText={
          estimateData?.prorated &&
          copy.tertiaryText({
            currencySymbol: estimateData.currencySymbol,
            proratedTotalAmount: estimateData.prorated.totalAmount,
            renewalCurrencyCode: estimateData.renewal.currencyCode,
            proratedCurrencyCode: estimateData.prorated.currencyCode,
          })
        }
        ctaText={copy.paymentCTAText}
        onSuccess={async () => {
          await submit();
        }}
      />
    </>
  );
};
